/**
 * The portal's query cache follows who is signed in (Addendum No. 3, Point 7).
 *
 * A cached answer is one user's view of the company, and since the Gestor Individual that view is not
 * everyone's: an administrator's list left in the cache would be shown to the Gestor who signs in after
 * them on a shared office PC. Not only for the thirty seconds it counts as fresh: once stale it is still
 * shown while it is fetched again, for as long as the cache keeps it — five minutes after the last
 * screen using it closes, TanStack Query's default `gcTime`. So the cache is emptied whenever the
 * signed-in identity changes: sign-in, sign-out, a session that ends, and a role changed under a live
 * session. A token refresh that changes nothing leaves it alone.
 *
 * A subscriber to the session store rather than a call inside `AuthProvider`: every path that changes
 * the principal goes through `session.ts`, and `AuthProvider`'s tests render it without a query client.
 * Choosing another company (a platform role) already clears the cache in `useOrganisationChoice`.
 */

import type { QueryClient } from '@tanstack/react-query'
import type { CurrentPrincipal } from '@deca/api'

import { getPrincipal, subscribeSession } from './session.ts'

/** Who is signed in, in the three respects that decide what the portal may show them. */
export function identityOf(principal: CurrentPrincipal | null): string {
  return principal ? [principal.id, principal.role ?? '', principal.organisation.id].join('|') : ''
}

/** Empties `client` whenever the signed-in identity changes. Returns the unsubscribe. */
export function clearCacheOnIdentityChange(client: QueryClient): () => void {
  let current = identityOf(getPrincipal())
  return subscribeSession(() => {
    const next = identityOf(getPrincipal())
    if (next === current) return
    current = next
    client.clear()
  })
}
